import React, { useState } from 'react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { PhoneMissed, ListChecks, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import SuccessCoachingQuestionsDialog from './SuccessCoachingQuestionsDialog';

const UnsuccessfulInfo = ({ templates }) => {
  const hasEmail = !!(templates.email_body || '').trim();
  const hasSms = !!(templates.sms_body || '').trim();
  const missing = [!(templates.case_note_body || '').trim() && 'case note', !hasEmail && 'email', !hasSms && 'text'].filter(Boolean);
  const sends = [hasEmail && 'email', hasSms && 'text'].filter(Boolean);
  const notSent = missing.filter(m => m !== 'case note');
  const list = (a) => a.length > 1 ? `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}` : a[0];
  return (
    <div className="space-y-1" data-testid="sc-unsuccessful-info">
      <p className="text-xs text-amber-700" data-testid="sc-unsuccessful-send-line">
        {sends.length ? `The unsuccessful contact ${sends.join(' and ')} will be sent to the participant after saving.` : 'No unsuccessful contact email or text will be sent.'}
        {' '}This contact is recorded as unsuccessful and does not count as a completed session.
      </p>
      {missing.length > 0 && (
        <p className="text-xs font-medium text-red-600" data-testid="sc-unsuccessful-missing">
          Missing the unsuccessful {list(missing)} template{missing.length > 1 ? 's' : ''} in the Success Coaching settings{notSent.length ? `, so the ${list(notSent)} will not be sent` : ''}.
        </p>
      )}
    </div>
  );
};

// Unsuccessful-call toggle and weekly questions for Success Coaching notes on the Caseload Review
export const SuccessCoachingNoteControls = ({ caseId, weekInfo, unsuccessful, onToggleUnsuccessful, answered, onQuestionsComplete }) => {
  const [showQuestions, setShowQuestions] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!weekInfo) return null;
  if (weekInfo.completed) {
    return (
      <div className="rounded-md border bg-gray-50 p-3 text-sm text-muted-foreground" data-testid="sc-program-complete-info">
        This participant has completed the 12-week Success Coaching program. Weekly questions and messages are not used.
      </div>
    );
  }

  const hasQuestions = (weekInfo.week?.questions || []).length > 0;

  return (
    <div className="space-y-3 rounded-md border border-green-200 bg-green-50/50 p-3" data-testid="sc-note-controls">
      <div className="min-w-0">
        <p className={`text-sm font-medium whitespace-pre-line ${expanded ? '' : 'line-clamp-2'}`} data-testid="sc-week-topic">
          Week {weekInfo.week_number}{weekInfo.topic ? `: ${weekInfo.topic}` : ''}
        </p>
        {(weekInfo.topic || '').length > 80 && (
          <button type="button" onClick={() => setExpanded(e => !e)} className="text-xs font-medium text-green-700 hover:underline inline-flex items-center gap-0.5" data-testid="sc-week-topic-toggle">
            {expanded ? <>Show less<ChevronUp className="w-3 h-3" /></> : <>Show more<ChevronDown className="w-3 h-3" /></>}
          </button>
        )}
        {!unsuccessful && <p className="text-xs text-muted-foreground">This week's email and text are sent to the participant after the note is saved.</p>}
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md bg-white border p-2">
        <Label htmlFor="sc-unsuccessful-toggle" className="flex items-center gap-2 text-sm cursor-pointer">
          <PhoneMissed className="w-4 h-4 text-amber-600" />
          Unsuccessful call attempt
        </Label>
        <Switch id="sc-unsuccessful-toggle" checked={unsuccessful} onCheckedChange={onToggleUnsuccessful} data-testid="sc-unsuccessful-toggle" />
      </div>
      {!unsuccessful && hasQuestions && (
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowQuestions(true)} data-testid="sc-open-questions-btn">
            <ListChecks className="w-4 h-4 mr-1" />{answered ? 'Redo' : 'Answer'} Week {weekInfo.week_number} Questions
          </Button>
          {answered && <Badge className="bg-green-600 hover:bg-green-600" data-testid="sc-questions-answered-badge"><CheckCircle2 className="w-3 h-3 mr-1" />Summary added</Badge>}
        </div>
      )}
      {unsuccessful && <UnsuccessfulInfo templates={weekInfo.unsuccessful || {}} />}
      <SuccessCoachingQuestionsDialog
        open={showQuestions}
        caseId={caseId}
        weekInfo={weekInfo}
        onComplete={(result) => { setShowQuestions(false); onQuestionsComplete(result); }}
        onSkip={() => setShowQuestions(false)}
      />
    </div>
  );
};
